import styled from 'styled-components'
import Page from 'organization/Event/Page'
import FormActions from 'organization/Event/Form/FormActions'
import QuestionsProvider, {
  Question,
  useQuestions,
  Option,
  isQuestionWithOptions,
} from 'organization/Event/QuestionsProvider'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import QuestionsList from 'organization/Event/Form/QuestionsList'
import {useForm as useEventForm} from 'organization/Event/Form/FormProvider'
import {Form as FormData} from 'organization/Event/FormsProvider'
import {Controller, useForm as useHookForm} from 'react-hook-form'
import Box from '@material-ui/core/Box'
import ActionSelect from 'Event/ActionsProvider/ActionSelect'
import ActionsProvider from 'Event/ActionsProvider'
import {InfusionsoftTag} from 'Event/infusionsoft'
import InfusionsoftTagInput from 'organization/Event/DashboardConfig/InfusionsoftTagInput'
import {fieldError} from 'lib/form'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import PageHeader from 'lib/ui/PageHeader'
import Title from 'lib/ui/PageHeader/Title'
import PageHeaderButton from 'lib/ui/PageHeader/Button'
import Checkbox from 'lib/ui/Checkbox'
import Button from 'lib/ui/Button'
import InputLabel from 'lib/ui/TextField/InputLabel'
import TextField from 'lib/ui/TextField'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import {
  ACTIVE_CAMPAIGN,
  CLICK_FUNNELS,
  CONVERT_KIT,
  HIGHLEVEL,
  HUBSPOT,
  HUBSPOTv2,
  INFUSIONSOFT,
  ONTRAPORT,
} from 'organization/Event/Services/ServicesProvider'
import {ZAPIER} from 'organization/Event/Services/ServicesProvider'
import {ZapierTag} from 'Event/zapier'
import {MAILCHIMP} from 'organization/Event/Services/ServicesProvider'
import {MailchimpTag} from 'Event/mailchimp'
import {ActiveCampaignTag} from 'Event/activeCampaign'
import {HubspotTag} from 'Event/hubspot'
import HubspotTagInput from 'organization/Event/DashboardConfig/HubspotTagInput'
import ZapierTagInput from 'organization/Event/DashboardConfig/ZapierTagInput'
import MailchimpTagInput from 'organization/Event/DashboardConfig/MailchimpTagInput'
import ActiveCampaignTagInput from 'organization/Event/DashboardConfig/ActiveCampaignTagInput'
import ServiceComponent from 'organization/Event/Services/ServiceComponent'
import {Label} from 'lib/ui/typography'
import HighLevelTagInput from 'organization/Event/DashboardConfig/HighLevelTagInput'
import {HighLevelTag} from 'Event/highlevel'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import ConvertKitTagInput from 'organization/Event/DashboardConfig/ConvertKitTagInput'
import {ConvertKitTag} from 'organization/Event/Services/Apps/ConvertKit/Config/Tags'
import OntraportTagInput from 'organization/Event/DashboardConfig/OntraportTagInput'
import {OntraportTag} from 'organization/Event/Services/Apps/Ontraport/Config/Tags'
import {useToggleArray} from 'lib/toggle'
import QuestionConfig from 'organization/Event/Form/dialog/Form/QuestionConfig'
import {
  useAddTranslation,
  useRemoveTranslationKeys,
  useTranslationLanguage,
} from 'Event/LanguageProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useAttendeeVariables} from 'Event'
import ConfigBar from 'organization/Event/TemplateEditor/ConfigBar'
import ConfirmUnsavedFormChangesDialog from 'organization/Event/Form/ConfirmUnsavedFormChangesDialog'
import {CLickFunnelsTag} from 'organization/Event/Services/Apps/ClickFunnels/Config/Tags'
import ClickFunnelsTagInput from 'organization/Event/DashboardConfig/ClickFunnelsTagInput'
import {useEvent} from 'Event/EventProvider'
import {Breadcrumb, WithBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import HubspotV2TagInput from 'organization/Event/DashboardConfig/HubspotV2TagInput'
import {HubspotV2Tag} from 'Event/hubspot-v2'
import {Rule} from 'Event/attendee-rules'
import TestClickFunnelsTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestClickFunnelsTagButton'
import TestOntraportTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestOntraportTagButton'
import TestConvertKitTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestConvertKitButton'
import TestActiveCampaignTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestActiveCampaignTagButton'
import TestHighLevelTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestHighLevelTagButton'
import TestHubspotV2TagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestHubspotV2TagButton'
import TestHubspotTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestHubspotTagButton'
import TestInfusionsoftTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestInfusionsoftTagButton'
import TestZapierTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestZapierTagButton'
import TestMailchimpTagButton from 'organization/Event/Form/TestTagButton/integration-buttons/TestMailchimpTagButton'
import WebhookTest from 'organization/Event/Form/WebhookTest'

export const DEFAULT_RESUBMIT_LABEL = 'Edit your answer'
export const DEFAULT_SUBMIT_LABEL = 'Submit'
export const DEFAULT_SUBMITTED_MESSAGE = 'Thank you'

export default function Form() {
  const {form, processing, responseError} = useEventForm()
  const [editing, setEditing] = useState<Question | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)

  const stopEditing = () => setEditing(null)

  /**
   * Save the form config. We want the question list OUTSIDE of the
   * html form to avoid triggering validation, so this is the
   * workaround to allow that.
   */
  const save = () => {
    if (!formRef.current) {
      return
    }

    formRef.current.dispatchEvent(new Event('submit'))
  }

  const handleFormUpdate = useCallback(() => {
    setHasChanges(true)
  }, [])

  return (
    <>
      <ConfirmUnsavedFormChangesDialog hasChanges={hasChanges} onSave={save} />
      <ActionsProvider
        loader={
          <Content>
            <FullPageLoader />
          </Content>
        }
      >
        <StyledConfigBar
          rightToolbarItems={
            <PageHeaderButton
              text="Save Changes"
              textCollapsed="Save"
              aria-label="save form"
              disabled={processing}
              onClick={save}
            />
          }
        />

        <QuestionsProvider form={form}>
          <Content>
            <>
              <EditQuestion
                question={editing}
                onClose={stopEditing}
                form={form}
                onEdit={() => setHasChanges(true)}
              />
              <PageHeader>
                <Title text="Edit Form" />
              </PageHeader>
              <ErrorAlert>{responseError?.message}</ErrorAlert>

              <Box mb={2}>
                <FormConfig
                  ref={formRef}
                  onUpdate={handleFormUpdate}
                  onSave={() => {
                    setHasChanges(false)
                  }}
                />
              </Box>
              <QuestionsBox>
                <Box mb={2}>
                  <AddQuestion
                    processing={processing}
                    form={form}
                    onAdd={() => {
                      setHasChanges(true)
                    }}
                  />
                </Box>
                <QuestionsList
                  onSelect={setEditing}
                  onChange={() => {
                    setHasChanges(true)
                  }}
                />
              </QuestionsBox>
              <StyledFormActions />
            </>
          </Content>
        </QuestionsProvider>
      </ActionsProvider>
    </>
  )
}

const FormConfig = React.forwardRef<
  HTMLFormElement,
  {
    onUpdate: () => void
    onSave: () => void
  }
>((props, ref) => {
  const {
    handleSubmit,
    control,
    errors: formErrors,
    register,
    watch,
    formState: {isDirty},
  } = useHookForm()
  const {form, update: updateForm, processing, responseError} = useEventForm()

  const targetLanguage = useTranslationLanguage()

  const {onUpdate, onSave} = props

  useEffect(() => {
    if (isDirty) {
      onUpdate()
    }
  }, [isDirty, onUpdate])

  const {
    infusionsoftTag,
    setInfusionsoftTag,
    hubspotTag,
    setHubspotTag,
    hubspotV2Tag,
    setHubspotV2Tag,
    zapierTag,
    setZapierTag,
    highLevelTag,
    setHighLevelTag,
    mailchimpTag,
    setMailchimpTag,
    activeCampaignTag,
    setActiveCampaignTag,
    convertKitTag,
    setConvertKitTag,
    ontraportTag,
    setOntraportTag,
    clickFunnelsTag,
    setClickFunnelsTag,
  } = useFormTags(form)

  const updateTemplate = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const v = useAttendeeVariables()
  const currentLanguage = useTranslationLanguage()
  const removeTranslationKeys = useRemoveTranslationKeys({
    targetLanguage: currentLanguage,
  })
  const {questions} = useQuestions()
  const {
    translations: questionTranslations,
    questions: translatedQuestions,
  } = getQuestionTranslations({form, questions, v})

  const submit = (data: Partial<FormData>) => {
    const withAttributes: Partial<FormData> = {
      ...data,
      // update questions translations
      questions: [...translatedQuestions],
      tags: createTags({
        infusionsoftTag,
        highLevelTag,
        activeCampaignTag,
        mailchimpTag,
        zapierTag,
        hubspotTag,
        hubspotV2Tag,
        convertKitTag,
        ontraportTag,
        clickFunnelsTag,
      }),
    }

    // Remove all previous form translations in case a Question has been
    // deleted. If we didn't remove all, the question translation
    // would be dangling with no reference.
    const removedCurrentTranslations =
      removeTranslationKeys(`form_${form.id}`)?.translations?.[
        currentLanguage
      ] ?? {}

    // update template with all translations at once, because each update is 1 request to the API
    // also that prevent from bug where it don't show translations
    updateTemplate({
      localization: addTranslation({
        ...removedCurrentTranslations,
        ...questionTranslations,
        [`form_${form.id}_submitted_message`]:
          withAttributes.submitted_message ?? '',
        [`form_${form.id}_resubmit_button_label`]:
          withAttributes.resubmit_button_label ?? '',
        [`form_${form.id}_submit_label`]: withAttributes.submit_label ?? '',
        [`form_${form.id}_field_required_message`]:
          withAttributes.field_required_message ?? '',
      }),
    })

    updateForm({
      ...withAttributes,
      tags: createTags({
        infusionsoftTag,
        highLevelTag,
        activeCampaignTag,
        mailchimpTag,
        zapierTag,
        hubspotTag,
        hubspotV2Tag,
        convertKitTag,
        ontraportTag,
        clickFunnelsTag,
      }),
      questions: translatedQuestions,
      submitted_message: `{{form_${form.id}_submitted_message}}`,
      resubmit_button_label: `{{form_${form.id}_resubmit_button_label}}`,
      submit_label: `{{form_${form.id}_submit_label}}`,
      field_required_message: `{{form_${form.id}_field_required_message}}`,
    })
    onSave()
  }

  const error = (key: keyof FormData) =>
    fieldError(key, {
      response: responseError,
      form: formErrors,
    })

  const nameError = error('name')
  const onSubmitRedirectUrlError = error('on_submit_redirect_url')
  const submissionWebhookUrlError = error('submission_webhook_url')
  const submitLabelError = error('submit_label')
  const submittedMessageError = error('submitted_message')
  const resubmitButtonLabelError = error('resubmit_button_label')
  const fieldRequiredMessageError = error('field_required_message')
  const canResubmit = watch('can_resubmit', form.can_resubmit)
  const allowsMultipleSubmissions = watch(
    'allows_multiple_submissions',
    form.allows_multiple_submissions,
  )

  return (
    <>
      <form onSubmit={handleSubmit(submit)} ref={ref}>
        <InputLabel>Form Name</InputLabel>
        <TextField
          aria-label="form name"
          name="name"
          defaultValue={form.name}
          inputProps={{
            ref: register,
          }}
          variant="outlined"
          required
          placeholder="Type form name..."
          fullWidth
          disabled={processing}
          helperText={nameError}
          error={Boolean(nameError)}
        />
        <StyledBox>
          <Controller
            control={control}
            name="can_resubmit"
            defaultValue={form.can_resubmit || false}
            render={({onChange, value}) => (
              <Checkbox
                checked={!!value}
                label="Can Edit Answer?"
                aria-label="toggle can re-submit"
                disabled={processing || allowsMultipleSubmissions}
                onChange={onChange}
              />
            )}
          />
        </StyledBox>
        <StyledBox>
          <Controller
            control={control}
            name="allows_multiple_submissions"
            defaultValue={form.allows_multiple_submissions}
            render={({onChange, value}) => (
              <Checkbox
                checked={!!value}
                label="Allow Multiple Submissions?"
                aria-label="toggle allow multiple submissions"
                disabled={processing || canResubmit}
                onChange={onChange}
              />
            )}
          />
        </StyledBox>

        <InputLabel disabled={allowsMultipleSubmissions || !canResubmit}>
          Resubmit Button Label
        </InputLabel>
        <TextField
          key={`${targetLanguage}_resubmit_button_label`} // rerender on language change
          variant="outlined"
          aria-label="resubmit button label"
          name="resubmit_button_label"
          inputProps={{
            ref: register,
          }}
          defaultValue={v(form.resubmit_button_label)}
          placeholder="Type resubmit button label..."
          fullWidth
          disabled={processing || allowsMultipleSubmissions || !canResubmit}
          helperText={resubmitButtonLabelError}
          error={Boolean(resubmitButtonLabelError)}
        />

        <InputLabel>Submit Label</InputLabel>
        <TextField
          key={`${targetLanguage}_submit_label`} // rerender on language change
          variant="outlined"
          aria-label="form submit label"
          name="submit_label"
          defaultValue={v(form.submit_label)}
          inputProps={{
            ref: register,
          }}
          placeholder="Type submit label..."
          fullWidth
          disabled={processing}
          helperText={submitLabelError}
          error={Boolean(submitLabelError)}
        />

        <InputLabel disabled={canResubmit}>Submitted Message</InputLabel>
        <TextField
          key={`${targetLanguage}_submitted_message`} // rerender on language change
          aria-label="form submitted message"
          name="submitted_message"
          defaultValue={v(form.submitted_message)}
          variant="outlined"
          placeholder="Type submitted message..."
          fullWidth
          disabled={processing || canResubmit}
          inputProps={{
            ref: register,
          }}
          helperText={
            submittedMessageError ||
            'Message displayed when form has been submitted'
          }
          error={Boolean(submittedMessageError)}
        />

        <InputLabel>Field required message</InputLabel>
        <TextField
          key={`${targetLanguage}_field_required_message`} // rerender on language change
          aria-label="field required message"
          name="field_required_message"
          defaultValue={v(form.field_required_message)}
          variant="outlined"
          placeholder="Type field required message..."
          fullWidth
          disabled={processing}
          inputProps={{
            ref: register,
          }}
          helperText={
            fieldRequiredMessageError ||
            'Message displayed when required field is not filled'
          }
          error={Boolean(fieldRequiredMessageError)}
        />

        <InputLabel>Redirect URL (optional)</InputLabel>
        <TextField
          variant="outlined"
          aria-label="redirect url after submit"
          name="on_submit_redirect_url"
          defaultValue={form.on_submit_redirect_url || ''}
          placeholder="Type redirect url after submit..."
          fullWidth
          inputProps={{
            ref: register,
          }}
          disabled={processing}
          helperText={
            onSubmitRedirectUrlError ||
            'URL to redirect to after completing form. Starting with https:// or http://.'
          }
          error={Boolean(onSubmitRedirectUrlError)}
        />

        <WebhookHolder>
          <div>
            <InputLabel>Submission Webhook URL (optional)</InputLabel>
            <TextField
              variant="outlined"
              aria-label="submission webhook url"
              name="submission_webhook_url"
              defaultValue={form.submission_webhook_url || ''}
              inputProps={{
                ref: register,
              }}
              placeholder="Type submission webhook URL..."
              fullWidth
              disabled={processing}
              helperText={
                submissionWebhookUrlError ||
                'Webhook URL to send submissions. Starting with https:// or http://.'
              }
              error={Boolean(submissionWebhookUrlError)}
            />
          </div>
          <div>
            <WebhookTest
              webhookURL={watch('submission_webhook_url')}
              form={form}
            />
          </div>
        </WebhookHolder>

        <StyledBox>
          <Controller
            control={control}
            name="action_id"
            defaultValue={form.action?.id || ''}
            render={({onChange, value}) => (
              <ActionSelect
                value={value}
                onChange={onChange}
                useId
                disabled={processing}
              />
            )}
          />
        </StyledBox>
      </form>

      <ServiceComponent service={INFUSIONSOFT}>
        <ServiceTagHolder>
          <div>
            <StyledLabel>Infusionsoft tag</StyledLabel>
            <InfusionsoftTagInput
              value={infusionsoftTag}
              onChange={setInfusionsoftTag}
              disabled={processing}
              autoSet
            />
          </div>
          <div>
            <TestInfusionsoftTagButton tag={infusionsoftTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={HUBSPOT}>
        <ServiceTagHolder>
          <div>
            <StyledLabel>Hubspot tag</StyledLabel>
            <HubspotTagInput
              value={hubspotTag}
              onChange={setHubspotTag}
              disabled={processing}
            />
          </div>
          <div>
            <TestHubspotTagButton tag={hubspotTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={HUBSPOTv2}>
        <ServiceTagHolder>
          <div>
            <HubspotV2TagInput
              value={hubspotV2Tag}
              onChange={setHubspotV2Tag}
              disabled={processing}
            />
          </div>
          <div>
            <TestHubspotV2TagButton tag={hubspotV2Tag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={ZAPIER}>
        <ServiceTagHolder>
          <div>
            <StyledLabel>Zapier tag</StyledLabel>
            <ZapierTagInput
              value={zapierTag}
              onChange={setZapierTag}
              disabled={processing}
              variant="outlined"
              helperText=""
            />
          </div>
          <div>
            <TestZapierTagButton tag={zapierTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={HIGHLEVEL}>
        <ServiceTagHolder>
          <div>
            <StyledLabel>High level tag</StyledLabel>
            <HighLevelTagInput
              value={highLevelTag}
              onChange={setHighLevelTag}
              disabled={processing}
              variant="outlined"
              helperText=""
            />
          </div>
          <div>
            <TestHighLevelTagButton tag={highLevelTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={ACTIVE_CAMPAIGN}>
        <ServiceTagHolder>
          <ActiveCampaignTagInput
            value={activeCampaignTag}
            onChange={setActiveCampaignTag}
            disabled={processing}
          />
          <div>
            <TestActiveCampaignTagButton tag={activeCampaignTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={CONVERT_KIT}>
        <ServiceTagHolder>
          <ConvertKitTagInput
            value={convertKitTag}
            onChange={setConvertKitTag}
            disabled={processing}
          />
          <div>
            <TestConvertKitTagButton tag={convertKitTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={MAILCHIMP}>
        <ServiceTagHolder>
          <div>
            <StyledLabel>Mailchimp tag</StyledLabel>
            <MailchimpTagInput
              value={mailchimpTag}
              onChange={setMailchimpTag}
              disabled={processing}
              variant="outlined"
            />
          </div>
          <div>
            <TestMailchimpTagButton tag={mailchimpTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>

      <ServiceComponent service={ONTRAPORT}>
        <ServiceTagHolder>
          <OntraportTagInput
            value={ontraportTag}
            onChange={setOntraportTag}
            disabled={processing}
          />
          <div>
            <TestOntraportTagButton tag={ontraportTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>
      <ServiceComponent service={CLICK_FUNNELS}>
        <ServiceTagHolder>
          <ClickFunnelsTagInput
            value={clickFunnelsTag}
            onChange={setClickFunnelsTag}
            disabled={processing}
          />
          <div>
            <TestClickFunnelsTagButton tag={clickFunnelsTag} />
          </div>
        </ServiceTagHolder>
      </ServiceComponent>
    </>
  )
})

const ServiceTagHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  > :first-child {
    flex: 1;
  }
`

const WebhookHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  > :first-child {
    flex: 1;
  }
`

function Content(props: {
  children: JSX.Element
  parent?: Breadcrumb
  page?: string
}) {
  const routes = useEventRoutes()
  const {form} = useEventForm()
  const {event} = useEvent()
  const eventRoutes = useEventRoutes()

  const parent = props.parent || {
    title: event.name,
    url: eventRoutes.root,
  }

  const breadcrumbs: Breadcrumb[] = [
    parent,
    {
      title: 'Forms',
      url: routes.forms.root,
    },
    {
      title: props.page || form.name,
    },
  ]

  return (
    <WithBreadcrumbs breadcrumbs={breadcrumbs}>
      <Page>{props.children}</Page>
    </WithBreadcrumbs>
  )
}

function useFormTags(form: FormData) {
  const [
    activeCampaignTag,
    setActiveCampaignTag,
  ] = useState<ActiveCampaignTag | null>(null)
  const [
    infusionsoftTag,
    setInfusionsoftTag,
  ] = useState<InfusionsoftTag | null>(null)
  const [zapierTag, setZapierTag] = useState<ZapierTag | null>(null)
  const [mailchimpTag, setMailchimpTag] = useState<MailchimpTag | null>(null)
  const [hubspotTag, setHubspotTag] = useState<HubspotTag | null>(null)
  const [hubspotV2Tag, setHubspotV2Tag] = useState<HubspotV2Tag | null>(null)
  const [highLevelTag, setHighLevelTag] = useState<HighLevelTag | null>(null)
  const [convertKitTag, setConvertKitTag] = useState<ConvertKitTag | null>(null)
  const [ontraportTag, setOntraportTag] = useState<OntraportTag | null>(null)
  const [
    clickFunnelsTag,
    setClickFunnelsTag,
  ] = useState<CLickFunnelsTag | null>(null)

  useEffect(() => {
    const {tags} = form

    setInfusionsoftTag(null)

    if (tags?.infusionsoft?.infusionsoft_id && tags.infusionsoft.name) {
      setInfusionsoftTag({
        id: tags.infusionsoft.infusionsoft_id,
        name: tags.infusionsoft.name,
      })
    }

    if (tags?.zapier && tags.zapier.name) {
      setZapierTag({
        name: tags.zapier.name,
      })
    }

    if (tags?.highlevel && tags.highlevel.name) {
      setHighLevelTag({
        name: tags.highlevel.name,
      })
    }

    if (tags?.mailchimp?.name) {
      setMailchimpTag({
        name: tags.mailchimp.name,
      })
    }

    if (tags?.activecampaign?.active_campaign_id && tags.activecampaign.name) {
      setActiveCampaignTag({
        id: tags.activecampaign.active_campaign_id,
        tag: tags.activecampaign.name,
      })
    }

    if (tags?.hubspot) {
      setHubspotTag(tags.hubspot)
    }

    if (tags?.hubspot_v2) {
      setHubspotV2Tag(tags.hubspot_v2)
    }

    if (tags?.convertkit?.id && tags.convertkit.name) {
      setConvertKitTag({
        id: tags.convertkit.id,
        name: tags.convertkit.name,
      })
    }

    if (tags?.ontraport && tags.ontraport.tag_id && tags.ontraport.tag_name) {
      setOntraportTag(tags.ontraport)
    }

    if (tags?.clickfunnels) {
      setClickFunnelsTag(tags.clickfunnels)
    }
  }, [form])

  return {
    infusionsoftTag,
    setInfusionsoftTag,
    activeCampaignTag,
    setActiveCampaignTag,
    zapierTag,
    setZapierTag,
    highLevelTag,
    setHighLevelTag,
    mailchimpTag,
    setMailchimpTag,
    hubspotTag,
    setHubspotTag,
    hubspotV2Tag,
    setHubspotV2Tag: setHubspotV2Tag,
    convertKitTag,
    setConvertKitTag,
    ontraportTag,
    setOntraportTag,
    clickFunnelsTag,
    setClickFunnelsTag,
  }
}

function createTags(props: {
  zapierTag: ZapierTag | null
  highLevelTag: HighLevelTag | null
  infusionsoftTag: InfusionsoftTag | null
  mailchimpTag: MailchimpTag | null
  hubspotTag: HubspotTag | null
  hubspotV2Tag: HubspotV2Tag | null
  activeCampaignTag: ActiveCampaignTag | null
  convertKitTag: ConvertKitTag | null
  ontraportTag: OntraportTag | null
  clickFunnelsTag: CLickFunnelsTag | null
}): FormData['tags'] {
  const result: FormData['tags'] = {}

  const {
    zapierTag,
    highLevelTag,
    infusionsoftTag,
    mailchimpTag,
    hubspotTag,
    hubspotV2Tag,
    activeCampaignTag,
    convertKitTag,
    ontraportTag,
    clickFunnelsTag,
  } = props

  if (zapierTag) {
    result['zapier'] = {
      name: zapierTag.name,
    }
  }

  if (highLevelTag) {
    result['highlevel'] = {
      name: highLevelTag.name,
    }
  }

  if (infusionsoftTag) {
    result['infusionsoft'] = {
      infusionsoft_id: infusionsoftTag.id,
      name: infusionsoftTag.name,
    }
  }

  if (infusionsoftTag) {
    result['infusionsoft'] = {
      infusionsoft_id: infusionsoftTag.id,
      name: infusionsoftTag.name,
    }
  }

  if (mailchimpTag) {
    result['mailchimp'] = {
      name: mailchimpTag.name,
    }
  }

  if (hubspotTag) {
    result['hubspot'] = {
      property_label: hubspotTag.property_label,
      property_name: hubspotTag.property_name,
      property_type: hubspotTag.property_type,
      property_value: hubspotTag.property_value,
    }
  }

  if (hubspotV2Tag) {
    result['hubspot_v2'] = {
      property_label: hubspotV2Tag.property_label,
      property_name: hubspotV2Tag.property_name,
      property_type: hubspotV2Tag.property_type,
      property_value: hubspotV2Tag.property_value,
    }
  }

  if (activeCampaignTag) {
    result['activecampaign'] = {
      active_campaign_id: activeCampaignTag.id,
      name: activeCampaignTag.tag,
    }
  }

  if (convertKitTag) {
    result['convertkit'] = {
      id: convertKitTag.id,
      name: convertKitTag.name,
    }
  }

  if (ontraportTag) {
    result['ontraport'] = ontraportTag
  }

  if (clickFunnelsTag) {
    result['clickfunnels'] = {
      id: clickFunnelsTag.id,
      name: clickFunnelsTag.name,
    }
  }

  if (Object.keys(result).length === 0) {
    return null
  }

  return result
}

function AddQuestion(props: {
  processing: boolean
  form: FormData
  onAdd: () => void
}) {
  const [showing, toggle] = useToggleArray()
  return (
    <>
      <QuestionConfig
        showing={showing}
        onClose={toggle}
        form={props.form}
        onUpdate={props.onAdd}
      />
      <Button
        variant="contained"
        color="primary"
        aria-label="add question"
        onClick={toggle}
        disabled={props.processing}
      >
        Add Question
      </Button>
    </>
  )
}

function EditQuestion(props: {
  form: FormData
  question: Question | null
  onClose: () => void
  onEdit: () => void
}) {
  const {question, onEdit} = props

  if (!question) {
    return null
  }

  return (
    <>
      <QuestionConfig
        question={question}
        form={props.form}
        onClose={props.onClose}
        showing
        onUpdate={onEdit}
      />
    </>
  )
}

interface UseGetQuestionTranslationsParams {
  form: FormData
  questions: Question[]
  v: (text?: string, fallback?: any) => string
}

type QuestionId = string
type TranslatedOptionValue = string
type translationKey = string
type TranslatedOptions = Record<
  QuestionId,
  Record<TranslatedOptionValue, translationKey>
>

export const getQuestionTranslations = (
  params: UseGetQuestionTranslationsParams,
) => {
  const {form, questions, v} = params

  const translations: Record<string, string> = {}
  const translatedOptionsByQuestionId: TranslatedOptions = {}

  // Translate all options first in case they are referenced in rules in other questions
  for (const [questionIndex, question] of questions.entries()) {
    if (!isQuestionWithOptions(question)) continue

    for (const [optionIndex, option] of question.options.entries()) {
      const valueKey = `form_${form.id}_question_${questionIndex}_option_${optionIndex}_value`

      if (!translatedOptionsByQuestionId[question.id]) {
        translatedOptionsByQuestionId[question.id] = {}
      }
      translatedOptionsByQuestionId[question.id][
        v(option.value)
      ] = `{{${valueKey}}}`
    }
  }

  const translatedQuestions: Question[] = []

  for (const [questionIndex, question] of questions.entries()) {
    const questionTranslationKey = `form_${form.id}_question_${questionIndex}`

    translations[`${questionTranslationKey}_label`] = v(question.label)
    translations[`${questionTranslationKey}_helper_text`] = v(
      question.helper_text ?? '',
    )

    if (!isQuestionWithOptions(question)) {
      translatedQuestions.push({
        ...question,
        label: `{{${questionTranslationKey}_label}}`,
        helper_text: `{{${questionTranslationKey}_helper_text}}`,
        rules: translateRules(translatedOptionsByQuestionId, question.rules),
      })
      continue
    }

    const translatedOptions: Option[] = []

    for (const [optionIndex, option] of question.options.entries()) {
      const optionTranslationKey = `form_${form.id}_question_${questionIndex}_option_${optionIndex}`
      const valueKey = `${optionTranslationKey}_value`

      translations[valueKey] = v(option.value)
      translatedOptions.push({
        ...option,
        value: `{{${valueKey}}}`,
      })
    }

    translatedQuestions.push({
      ...question,
      options: translatedOptions,
      label: `{{${questionTranslationKey}_label}}`,
      helper_text: `{{${questionTranslationKey}_helper_text}}`,
      rules: translateRules(translatedOptionsByQuestionId, question.rules),
    })
  }

  return {
    questions: translatedQuestions,
    translations: translations,
  }
}

function translateRules(
  translatedOptions: TranslatedOptions,
  rules?: Rule[],
): Rule[] {
  if (!rules) {
    return []
  }

  return rules.map((rule) => {
    if (rule.source === 'Nested Rule') {
      return {
        ...rule,
        rules: translateRules(translatedOptions, rule.rules),
      }
    }

    if (rule.source !== 'Form Response') {
      return rule
    }

    const questionOptions = translatedOptions[rule.question_id]
    if (!questionOptions) {
      return rule
    }

    const translatedValue = questionOptions[rule.value]
    if (!translatedValue) {
      return rule
    }

    return {
      ...rule,
      value: translatedValue,
    }
  })
}

const StyledFormActions = styled(FormActions)`
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const StyledBox = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[6]};
  width: 300px;
`

const QuestionsBox = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[8]};
  max-width: 640px;
`

const StyledLabel = styled(Label)`
  margin-bottom: ${(props) => props.theme.spacing[4]} !important;
`

const StyledConfigBar = styled(ConfigBar)`
  max-height: 50px;
`
